import React, { useContext, useEffect, useState } from 'react';
import { callFunction, SendTeamsNotification } from '../../lib/Helpers';
import { useAppInsightsContext } from '@microsoft/applicationinsights-react-js';
import {
  IUserDocument,
  UserSettingsTable,
} from '../../widgets/UserSettingsTable';
import { useBoolean } from '@fluentui/react-hooks';
import { Panel, Theme, Spinner } from '@fluentui/react';
import { AppObject } from '../../widgets/AppCard';
import { ManageUserPanelFooter, UserSettingsContainer } from './styles';
import { ManageUserPanel } from '../../widgets/ManageUserPanel';
import { BitButton } from '../../widgets/BitButton';
import { EntObject } from '../../pages/MyHub';
import { TeamsFxContext } from '../../Context';
import { Alert } from '@fluentui/react-components/unstable';
import { ArrowClockwise } from '@phosphor-icons/react';
import { Tag } from '../../widgets/Tag';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { TutorialPopover } from '../../widgets/TutorialPopover';
import { HelpPanel } from '../../widgets/HelpPanel';

type UsersProps = {
  appsObj: AppObject[];
  entObj: EntObject;
  currentUser: string;
  tid: string;
  theme?: Theme;
  themeName?: string;
  onReloadRequest: React.Dispatch<void>;
  refreshLoading: boolean;
  tutorials?: string[];
};

type UserSubChageAction = {
  subid: string;
  name: string;
  userType: string;
};

const Users: React.FC<UsersProps> = (props: UsersProps) => {
  const appInsights = useAppInsightsContext();

  const teamsUserCredential = useContext(TeamsFxContext).teamsUserCredential;

  const [isPopoverVisible, setIsPopoverVisible] = useState(
    !props.tutorials?.includes('users-settings')
  );

  const { t } = useTranslation('settings');

  const container = {
    margin: '10 5 10 0',
    width: '100%',
  };

  const [isOpen, { setTrue: openPanel, setFalse: dismissPanel }] =
    useBoolean(false);
  const [savingError, setSavingError] = useState<string | undefined>(undefined);
  const [savingWarning, setSavingWarning] = useState<string | undefined>(
    undefined
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUserDocument>();
  const [savedAppsObj, setAppsObj] = useState(props.appsObj);
  const [tableKey, setRefreshTable] = useState('initial');
  const [userSubChanges, setSubChanges] = useState<UserSubChageAction[]>([]);
  const [hasAddedUser, setHasAddedUser] = useState(false);

  const onPanelCloseClicked = () => {
    setSubChanges([]);
    dismissPanel();
    setHasAddedUser(false);
  };

  const onComboChanged = (subid: string, name: string, option: string) => {
    if (!userSubChanges?.find((value) => value.subid === subid)) {
      setSubChanges([
        ...userSubChanges,
        { subid: subid, name: name, userType: option },
      ]);
    } else {
      const item: UserSubChageAction = {
        subid: subid,
        name: name,
        userType: option,
      };

      const changes = userSubChanges.filter((value) => value.subid !== subid);
      changes.push(item);

      setSubChanges(changes);
    }
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      if (userSubChanges && userSubChanges.length > 0) {
        setIsDirty(true);
      } else {
        setIsDirty(false);
      }
    }

    return () => {
      mounted = false;
    };
  }, [userSubChanges]);

  const onRenderFooterContent = React.useCallback(() => {
    const onSaveClicked = () => {
      if (isDirty) {
        setIsSaving(true);

        if (selectedUser && userSubChanges) {
          userSubChanges.forEach((element) => {
            let data: any;
            callFunction(
              true,
              'getUserProfile?{0}&uid=' + selectedUser.key,
              undefined,
              false,
              teamsUserCredential
            )
              .then((conts) => {
                data = {
                  subid: element.subid,
                  updateType: 'single',
                  userType:
                    element.userType === 'none' ? 'remove' : element.userType,
                  userId: selectedUser.key,
                  contacts: [
                    {
                      name: conts.displayName,
                      emailId: conts.mail,
                      objectId: conts.id,
                      tenantId: props.tid,
                    },
                  ],
                };
              })
              .then(() => {
                callFunction(
                  true,
                  'patchSubscriptionUsers',
                  JSON.stringify(data),
                  undefined,
                  teamsUserCredential
                )
                  .then((data) => {
                    setSavingWarning('saving');
                    setSavingError(undefined);
                    setIsSaving(false);
                    setSubChanges([]);
                    if (
                      selectedUser.key !== props.currentUser &&
                      element.userType !== 'none'
                    )
                      SendTeamsNotification(
                        selectedUser?.key as string,
                        element.name
                      ).catch((error) => {
                        if (process.env.REACT_APP_APP_INSIGHTS !== '')
                          appInsights.trackException({
                            exception: error,
                            properties: {
                              component: 'Users',
                              location: 'onSendTeamsNotification',
                            },
                          });
                      });
                    dismissPanel();
                    toast.success('User added successfully!');
                    props.onReloadRequest();
                    setSavingWarning(undefined);
                    setHasAddedUser(false);
                  })
                  .catch((error) => {
                    if (process.env.REACT_APP_APP_INSIGHTS !== '')
                      appInsights.trackException({
                        exception: error,
                        properties: {
                          component: 'Users',
                          location: 'onPatchSubscriptionUsers',
                        },
                      });
                    setSavingError(error);
                    setIsSaving(false);
                    setSubChanges([]);
                    dismissPanel();
                  });
              })
              .catch((error) => {
                if (process.env.REACT_APP_APP_INSIGHTS !== '')
                  appInsights.trackException({
                    exception: error,
                    properties: {
                      component: 'Users',
                      location: 'onLoadContactData',
                    },
                  });
              });
          });
        } else {
          setSavingError('Unable to save changes ');
          setIsSaving(false);
          setSubChanges([]);
          dismissPanel();
        }
      } else {
        dismissPanel();
      }
    };

    const onCancelClicked = () => {
      setSubChanges([]);
      dismissPanel();
      setHasAddedUser(false);
    };

    return (
      <ManageUserPanelFooter>
        <BitButton
          label={t('FOOTER_SAVE_BUTTON')}
          action={onSaveClicked}
          disabled={!isDirty}
        />
        <BitButton
          label={t('FOOTER_CANCEL_BUTTON')}
          variant="secondary"
          action={onCancelClicked}
        />
        {isSaving && <Spinner />}
      </ManageUserPanelFooter>
    );
  }, [
    appInsights,
    dismissPanel,
    isDirty,
    isSaving,
    props,
    selectedUser,
    t,
    teamsUserCredential,
    userSubChanges,
  ]);

  const onManageItem = (item: IUserDocument) => {
    setSelectedUser(item);
    setSavingError(undefined);
    openPanel();
  };

  return (
    <UserSettingsContainer>
      <h1>{t('USERS_MANAGEMENT_TITLE')}</h1>
      <div id="internaluserscontainer">
        {savingError && (
          <Alert
            intent="error"
            action={{
              icon: <ArrowClockwise color="#fd6262" />,
              onClick: () => window.location.reload(),
            }}
          >
            {t('SUBSCRIPTIONS_SAVING_ERROR')}
          </Alert>
        )}
        {savingWarning && (
          <Alert intent="info">{t('USERS_REFRESHING_DATABASE')}</Alert>
        )}
        <div style={container}>
          <UserSettingsTable
            key={tableKey}
            appsObj={savedAppsObj}
            entObj={props.entObj}
            onManageItem={onManageItem}
            theme={props.theme}
            themeName={props.themeName}
            teamsUserCredential={teamsUserCredential}
            addedUsers={hasAddedUser}
            setHasAddedUser={setHasAddedUser}
            refreshLoading={props.refreshLoading}
          />
        </div>
      </div>
      <Panel
        className="panelManage"
        isOpen={isOpen}
        onDismiss={onPanelCloseClicked}
        headerText={t('PANEL_USER_LICENCES_HEADER')}
        closeButtonAriaLabel="Close"
        onRenderFooterContent={onRenderFooterContent}
        isFooterAtBottom={true}
      >
        {isDirty && (
          <Tag
            content={t('PANEL_CHANGES_NOT_SAVED')}
            color="orange"
            variant="outlined"
          />
        )}
        <ManageUserPanel
          selectedUser={selectedUser}
          themeName={props.themeName ?? 'default'}
          apps={props.appsObj}
          entitlements={props.entObj}
          currentUser={props.currentUser}
          onComboChanged={onComboChanged}
        />
      </Panel>
      <HelpPanel
        appId="users-settings"
        firstRunEnabled={!props.tutorials?.includes('users-settings')}
      />

      <TutorialPopover
        appId="users-settings"
        scope="users-settings"
        dimissPopover={() => setIsPopoverVisible(false)}
        title="Users Management"
        isVisible={isPopoverVisible}
      />
    </UserSettingsContainer>
  );
};

export default Users;
